import { c as _c } from "react/compiler-runtime";
import { type UsedInConfig } from 'src/channelGrouping/types';
import { keepPreviousData, useMutation, type UseMutationResult, useQueries, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import * as analytics from 'shared/api/analytics';
import * as channelGrouping from 'shared/api/channelGroupings';
import * as clients from 'shared/api/clients';
import type * as fetch from 'shared/api/fetch';
import { formatDateToString } from 'shared/dateFns';
export const queryKeys = {
  channelGroupings: 'channelgroupings',
  channelGrouping: 'channelgrouping',
  channelGroupingPerformanceData: 'channelGroupingPerformanceData',
  userDetailsByEmails: 'userDetailsByEmails'
};
function shouldFetchChannelGroupingById(channelGroupingId: number | undefined): boolean {
  return channelGroupingId !== undefined && Number.isFinite(channelGroupingId) && channelGroupingId > 0;
}
type BulkDeleteResult = Array<{
  configId: number;
  success: boolean;
  error?: fetch.ClientError;
}>;
export const useQueryChannelGroupings = clientId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== clientId) {
    t0 = {
      queryKey: [queryKeys.channelGroupings, clientId],
      queryFn: async () => await channelGrouping.getChannelGroupings(clientId)
    };
    $[0] = clientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
};
export const useQueryChannelGrouping = (channelGroupingId, clientId, t0) => {
  const $ = _c(11);
  const allChannels = t0 === undefined ? false : t0;
  let t1;
  let t2;
  if ($[0] !== allChannels || $[1] !== channelGroupingId || $[2] !== clientId) {
    t1 = [queryKeys.channelGrouping, channelGroupingId, clientId, allChannels];
    t2 = async () => await channelGrouping.getChannelGrouping(channelGroupingId as number, clientId, false, allChannels);
    $[0] = allChannels;
    $[1] = channelGroupingId;
    $[2] = clientId;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== channelGroupingId) {
    t3 = shouldFetchChannelGroupingById(channelGroupingId);
    $[5] = channelGroupingId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchOnWindowFocus: false
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
};
export const useQueriesChannelGroupings = (channelGroupingIds, clientId, t0) => {
  const $ = _c(9);
  const allChannels = t0 === undefined ? false : t0;
  let t1;
  if ($[0] !== allChannels || $[1] !== channelGroupingIds || $[2] !== clientId) {
    let t2;
    if ($[4] !== allChannels || $[5] !== clientId) {
      t2 = channelGroupingId => ({
        queryKey: [queryKeys.channelGrouping, channelGroupingId, clientId, allChannels],
        queryFn: async () => await channelGrouping.getChannelGrouping(channelGroupingId, clientId, false, allChannels),
        enabled: shouldFetchChannelGroupingById(channelGroupingId),
        refetchOnWindowFocus: false
      });
      $[4] = allChannels;
      $[5] = clientId;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = channelGroupingIds.map(t2);
    $[0] = allChannels;
    $[1] = channelGroupingIds;
    $[2] = clientId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = {
      queries: t1
    };
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useQueries(t2);
};
export const useMutationCreateChannelGrouping = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onSuccess: async data_0 => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.channelGroupings, data_0.clientId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationUpdateChannelGrouping = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp2,
      onMutate: async newData => {
        const oldData = queryClient.getQueryData([queryKeys.channelGrouping, newData.id, newData.clientId]);
        queryClient.setQueryData([queryKeys.channelGrouping, newData.id, newData.clientId], newData);
        return {
          oldData
        };
      },
      onError: (_, variables, context) => {
        queryClient.setQueryData([queryKeys.channelGrouping, variables.id, variables.clientId], context?.oldData);
      },
      onSettled: async (__0, __, variables_0) => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.channelGrouping, variables_0.id, variables_0.clientId]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.channelGroupingPerformanceData, variables_0.id, variables_0.clientId]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.channelGroupings, variables_0.clientId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationDeleteChannelGrouping = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp3,
      onSuccess: async (_, variables) => {
        queryClient.removeQueries({
          queryKey: [queryKeys.channelGrouping, variables.configId, variables.clientId]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.channelGroupings, variables.clientId]
        });
      },
      onError: async (__0, variables_0) => {
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.channelGroupings, variables_0.clientId]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useMutationBulkDeleteChannelGroupings = t0 => {
  const $ = _c(8);
  const {
    clientId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId) {
    t1 = async accountIds => {
      const deletePromises = accountIds.map(async configId => {
        ;
        try {
          await channelGrouping.deleteChannelGrouping({
            clientId,
            configId
          });
          return {
            configId,
            success: true
          };
        } catch (t2) {
          const error = t2;
          return {
            configId,
            success: false,
            error: error as fetch.ClientError
          };
        }
      });
      return await Promise.all(deletePromises);
    };
    $[0] = clientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== clientId || $[3] !== queryClient) {
    t2 = async (_, configIds) => {
      configIds.forEach(configId_0 => {
        queryClient.removeQueries({
          queryKey: [queryKeys.channelGrouping, configId_0, clientId]
        });
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.channelGroupings, clientId]
      });
    };
    $[2] = clientId;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useMutation(t3);
};
export const useQueryChannelGroupingPerformanceData = t0 => {
  const $ = _c(40);
  const {
    report,
    clientId,
    channelGroupingId,
    currencyCode,
    fields,
    filters,
    dateStart,
    dateEnd,
    useCache: t1,
    enabled,
    pageSize,
    pageNum,
    orderBy
  } = t0;
  const useCache = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== fields) {
    t2 = fields.map(_temp4).join(",");
    $[0] = fields;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== dateStart) {
    t3 = dateStart.toISOString();
    $[2] = dateStart;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== dateEnd) {
    t4 = dateEnd.toISOString();
    $[4] = dateEnd;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== filters) {
    t5 = filters?.map(_temp5).join(",");
    $[6] = filters;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = orderBy?.direction;
  let t7;
  if ($[8] !== channelGroupingId || $[9] !== clientId || $[10] !== currencyCode || $[11] !== orderBy || $[12] !== pageNum || $[13] !== pageSize || $[14] !== report || $[15] !== t2 || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== useCache) {
    t7 = [queryKeys.channelGroupingPerformanceData, channelGroupingId, clientId, report, t2, t3, t4, t5, t6, useCache, pageNum, pageSize, currencyCode, orderBy];
    $[8] = channelGroupingId;
    $[9] = clientId;
    $[10] = currencyCode;
    $[11] = orderBy;
    $[12] = pageNum;
    $[13] = pageSize;
    $[14] = report;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = useCache;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== channelGroupingId || $[23] !== clientId || $[24] !== currencyCode || $[25] !== dateEnd || $[26] !== dateStart || $[27] !== fields || $[28] !== filters || $[29] !== orderBy || $[30] !== pageNum || $[31] !== pageSize || $[32] !== report || $[33] !== useCache) {
    t8 = async () => (await analytics.postChannelGroupingReport({
      id: channelGroupingId.toString(),
      report,
      clientId,
      targetCurrencyCode: currencyCode,
      fields,
      limit: pageSize,
      cursor: pageNum != null && pageSize != null ? pageNum * pageSize : undefined,
      groupBy: fields.filter(_temp6).map(_temp7),
      filters: [{
        field: "date",
        operator: "BETWEEN",
        value: [formatDateToString({
          date: dateStart,
          targetFormat: "yyyy-MM-dd"
        }), formatDateToString({
          date: dateEnd,
          targetFormat: "yyyy-MM-dd"
        })]
      }, ...(filters ?? [])],
      useCache,
      orderBy
    })) as unknown as analytics.AnalyticsResp;
    $[22] = channelGroupingId;
    $[23] = clientId;
    $[24] = currencyCode;
    $[25] = dateEnd;
    $[26] = dateStart;
    $[27] = fields;
    $[28] = filters;
    $[29] = orderBy;
    $[30] = pageNum;
    $[31] = pageSize;
    $[32] = report;
    $[33] = useCache;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  const t9 = pageNum == null ? undefined : keepPreviousData;
  let t10;
  if ($[35] !== enabled || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      refetchOnWindowFocus: false,
      enabled,
      placeholderData: t9,
      staleTime: Infinity,
      gcTime: Infinity,
      throwOnError: false
    };
    $[35] = enabled;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return useQuery(t10);
};
export const useQueriesUserProfilesFromUsedInConfigs = (usedInConfigs, clientId) => {
  const $ = _c(7);
  let t0;
  if ($[0] !== clientId || $[1] !== usedInConfigs) {
    let t1;
    if ($[3] !== clientId) {
      t1 = config_0 => ({
        queryKey: [queryKeys.userDetailsByEmails, config_0.updatedBy, clientId],
        queryFn: async () => await clients.getUserDetailsByEmail(config_0.updatedBy, clientId),
        staleTime: Infinity,
        gcTime: Infinity,
        refetchOnWindowFocus: false
      });
      $[3] = clientId;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = usedInConfigs.filter(_temp8).map(t1);
    $[0] = clientId;
    $[1] = usedInConfigs;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[5] !== t0) {
    t1 = {
      queries: t0
    };
    $[5] = t0;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return useQueries(t1);
};
async function _temp(data) {
  return await channelGrouping.createChannelGrouping(data);
}
async function _temp2(data) {
  return await channelGrouping.updateChannelGrouping(data);
}
async function _temp3(data) {
  return await channelGrouping.deleteChannelGrouping(data);
}
function _temp4(field) {
  return field.name;
}
function _temp5(filter) {
  return filter.field + filter.operator + (filter.value ?? "").toString();
}
function _temp6(field_0) {
  return field_0.aggregation == null;
}
function _temp7(field_1) {
  return field_1.name;
}
function _temp8(config) {
  return config.updatedBy != null;
}